import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TenantService } from './tenant.service';

/**
 * v1.6.1441 — the staff notification inbox (Asana 1217416837859037).
 *
 * Backs the header bell. Abdelrhman's framing is the design constraint worth
 * repeating: **push is best effort, the inbox is the reliable surface** — so
 * nothing here may depend on a push having arrived. The badge is a polled count,
 * not a push side-effect.
 *
 * Every endpoint is SELF-SCOPED: no permission is required, and a caller only
 * ever sees rows whose `notifiable_id` is them. That is why there is no
 * permission gate anywhere in this service or the bell.
 */

export type NotificationStatus = 'unread' | 'read' | 'all';

export interface TenantNotificationRow {
  id: string;
  title?: string | null;
  message?: string | null;
  read_at?: string | null;
  created_at?: string | null;
  data?: {
    type?: string;
    inbound_message_id?: number | null;
    customer_id?: number | null;
    customer_name?: string | null;
    excerpt?: string | null;
    received_at?: string | null;
    /** RELATIVE path — prefix with this dashboard's own origin. */
    deep_link?: string | null;
    [k: string]: any;
  } | null;
}

/** One row of `GET /v1/tenant/notification-preferences`, as the contract names it. */
export interface StaffNotificationPreference {
  type: string;
  label: string;
  push_enabled: boolean;
  /** null (or empty) = every store. Always null when supports_store_scope is false. */
  store_ids: number[] | null;
  supports_store_scope: boolean;
}

/** One entry of the PUT body. `store_ids` only for a store-scoped type. */
export interface PreferenceUpdate {
  type: string;
  push_enabled: boolean;
  store_ids?: number[] | null;
}

/**
 * The rows exactly as the server sent them. No defaults are merged in and no
 * type is added or dropped — the contract says to render the response as-is,
 * and that a missing type does not mean "off".
 */
export function readPreferences(res: any): StaffNotificationPreference[] {
  const rows = res?.data ?? res;
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r: any) => r && typeof r.type === 'string' && r.type)
    .map((r: any) => ({
      type: r.type,
      label: String(r.label ?? r.type),
      push_enabled: r.push_enabled === true,
      store_ids: normaliseStoreIds(r.store_ids),
      supports_store_scope: r.supports_store_scope === true,
    }));
}

/** Sorted unique ids, or null for "every store" (null and [] mean the same). */
export function normaliseStoreIds(ids: any): number[] | null {
  if (!Array.isArray(ids)) return null;
  const clean = Array.from(new Set(ids.map((n: any) => Number(n))
    .filter((n: number) => Number.isInteger(n) && n > 0))).sort((a, b) => a - b);
  return clean.length ? clean : null;
}

export function sameStoreScope(a: number[] | null, b: number[] | null): boolean {
  const x = normaliseStoreIds(a), y = normaliseStoreIds(b);
  if (x === null || y === null) return x === y;
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/**
 * The PUT body's entries: ONLY the rows that differ from what the server last
 * returned. An untouched page therefore sends nothing at all.
 *
 * A changed store-scoped row always carries its `store_ids` — the current
 * value, which is the GET's value unless the user changed it. Omitting it is
 * not safe: the contract defines `store_ids` as nullable, so a missing key
 * could be read as null and silently widen a narrowed alert to every store.
 */
export function changedPreferences(
  baseline: StaffNotificationPreference[],
  draft: StaffNotificationPreference[],
): PreferenceUpdate[] {
  const before = new Map(baseline.map(r => [r.type, r]));
  const out: PreferenceUpdate[] = [];
  for (const row of draft) {
    const was = before.get(row.type);
    if (!was) continue;   // never send a type the server did not offer
    const pushChanged = was.push_enabled !== row.push_enabled;
    const storesChanged = row.supports_store_scope && !sameStoreScope(was.store_ids, row.store_ids);
    if (!pushChanged && !storesChanged) continue;
    const entry: PreferenceUpdate = { type: row.type, push_enabled: row.push_enabled };
    if (row.supports_store_scope) entry.store_ids = normaliseStoreIds(row.store_ids);
    out.push(entry);
  }
  return out;
}

@Injectable({ providedIn: 'root' })
export class TenantNotificationsService {
  constructor(private http: HttpClient, private tenant: TenantService) {}

  private get base(): string { return `${this.tenant.apiBaseUrl}/v1/tenant/notifications`; }

  /**
   * The badge count. A dedicated endpoint precisely so the bell never pulls the
   * whole list just to count it — Abdelrhman calls that out explicitly.
   */
  unreadCount(): Observable<number> {
    return this.http.get<any>(`${this.base}/unread-count`).pipe(
      map(res => {
        // Tolerate the count sitting at either depth rather than guessing.
        const d = res?.data ?? res;
        const n = d?.unread_count ?? d?.count ?? d;
        return typeof n === 'number' ? n : 0;
      }),
      // A failed poll must never break the header. Keep the last known badge by
      // reporting -1, which the caller treats as "leave it alone".
      catchError(() => of(-1)),
    );
  }

  /**
   * The list behind the bell.
   *
   * TRAP: the rows sit **one level deeper than usual** — `data.data`, not
   * `data`. Reading `res.data` here yields the paginator object, and the list
   * renders empty with no error.
   */
  list(params: { status?: NotificationStatus; type?: string; page?: number; per_page?: number } = {}):
      Observable<{ rows: TenantNotificationRow[]; lastPage: number; total: number }> {
    const query: any = {};
    if (params.status && params.status !== 'all') query.status = params.status;
    if (params.status === 'all') query.status = 'all';
    if (params.type) query.type = params.type;
    query.page = params.page ?? 1;
    query.per_page = params.per_page ?? 15;

    return this.http.get<any>(this.base, { params: query }).pipe(
      map(res => {
        const outer = res?.data ?? res;
        const rows = (outer?.data ?? outer?.rows ?? []) as TenantNotificationRow[];
        const meta = outer?.meta ?? outer;
        return {
          rows: Array.isArray(rows) ? rows : [],
          lastPage: meta?.last_page ?? 1,
          total: meta?.total ?? (Array.isArray(rows) ? rows.length : 0),
        };
      }),
    );
  }

  /**
   * Mark one row read.
   *
   * A **404 here is normal** — it means the row is not theirs or already gone.
   * Callers re-fetch instead of showing an error, so this maps 404 to a benign
   * result rather than an error path.
   */
  markRead(id: string): Observable<{ ok: boolean; gone: boolean }> {
    return this.http.patch<any>(`${this.base}/${id}/read`, {}).pipe(
      map(() => ({ ok: true, gone: false })),
      catchError((e: any) => of({ ok: false, gone: e?.status === 404 })),
    );
  }

  /** Mark everything read in one call — backs the "Mark all" button. */
  markAllRead(): Observable<boolean> {
    return this.http.post<any>(`${this.base}/read-all`, {}).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  /**
   * Mark several read. **Max 200 ids per call**, so the caller's list is
   * chunked here rather than trusting it to be short.
   *
   * The returned `marked` can legitimately be LOWER than `requested`: rows
   * already read, or not the caller's, are skipped silently. That is not a
   * failure and must not be surfaced as one.
   */
  // ── Asana 1220202714832233 — my notification preferences ───────────────
  //
  // GET/PUT /v1/tenant/notification-preferences. Self-scoped like the inbox:
  // no permission, and the write is always against the caller.
  //  - GET returns EVERY switchable type, defaults included; render it as-is.
  //  - PUT MERGES: "send only the entries you are changing; anything omitted is
  //    left as it is", and answers with the FULL refreshed list.
  //  - The switch governs PUSH only; store_ids also filters the inbox.

  private get prefsUrl(): string { return `${this.tenant.apiBaseUrl}/v1/tenant/notification-preferences`; }

  getPreferences(): Observable<StaffNotificationPreference[]> {
    return this.http.get<any>(this.prefsUrl).pipe(map(res => readPreferences(res)));
  }

  /** Callers must send only changed entries; see `changedPreferences`. */
  updatePreferences(preferences: PreferenceUpdate[]): Observable<StaffNotificationPreference[]> {
    return this.http.put<any>(this.prefsUrl, { preferences }).pipe(map(res => readPreferences(res)));
  }

  static readonly MARK_MANY_LIMIT = 200;

  markManyRead(ids: string[]): Observable<{ requested: number; marked: number }> {
    const unique = Array.from(new Set((ids ?? []).filter(Boolean)));
    if (!unique.length) return of({ requested: 0, marked: 0 });
    const batch = unique.slice(0, TenantNotificationsService.MARK_MANY_LIMIT);
    return this.http.post<any>(`${this.base}/read`, { ids: batch }).pipe(
      map(res => {
        const d = res?.data ?? res;
        return {
          requested: d?.requested ?? batch.length,
          marked: d?.marked ?? 0,
        };
      }),
      catchError(() => of({ requested: batch.length, marked: 0 })),
    );
  }
}

/**
 * Resolve a row's click target.
 *
 * TWO guards Abdelrhman flagged, both from real data:
 *  - Some rows have **no customer** (an unknown number replied). The name reads
 *    as the number and the deep link points at the list instead, so the click
 *    handler must not assume a customer id exists.
 *  - `deep_link` is a **relative path**. It is stored relative deliberately —
 *    a persisted absolute URL rots when the dashboard host changes — so the
 *    caller prefixes its own origin. Never treat it as an external URL.
 */
export function notificationTarget(row: TenantNotificationRow): string | null {
  const link = (row?.data?.deep_link ?? '').toString().trim();
  if (link) return link.startsWith('/') ? link : `/${link}`;
  // Asana 1220202714832233 — a suspicious-order reminder opens its order:
  // "from deep_link (/orders/{id}), or from data.order_id". Scoped to this
  // type so no existing kind changes where it goes.
  if ((row?.data?.type ?? '') === REVIEW_WAITING_TYPE) {
    const orderId = Number(row?.data?.['order_id']);
    if (Number.isInteger(orderId) && orderId > 0) return `/orders/${orderId}`;
  }
  const customerId = row?.data?.customer_id;
  if (customerId != null) return `/customers/${customerId}`;
  return null;
}

/**
 * Asana 1220202714832233 — reminders for a suspicious order left waiting for
 * review. `reason` is waiting | delivery_day | review_window, but the row's
 * `title` and `message` are already worded for it (including the "already
 * passed" wording), so the bell shows them as sent and never builds text
 * from `reason`.
 */
export const REVIEW_WAITING_TYPE = 'order.review_waiting';

/**
 * Display name, falling back to whatever the payload could identify.
 *
 * ── v1.6.1771 — Asana 1219431449527924 ───────────────────────────────────
 *
 * "the alerts at the top of the admin panel say unknown number for every
 * order". They did: this read `customer_name` and nothing else, and an ORDER
 * notification does not carry that key — it was written for the texting inbox,
 * where an unknown number really is the subject. Every order alert therefore
 * fell to the "Unknown number" branch, which is both wrong and alarming,
 * since it reads as "we don't know who this is" about a customer the order
 * names perfectly well.
 *
 * It now works down the names an order payload may carry before giving up,
 * and only then falls back to the number — which is at least an identity —
 * before the last-resort text. The spellings are the ones already in use
 * elsewhere in this codebase for the same idea, rather than invented.
 */
/**
 * ── v1.6.1792 — the bell is not a texting inbox any more ─────────────────
 *
 * Asana 1219431449527924. Angelo: "the alerts at the top of the admin panel
 * say unknown number for every order", and after v1.6.1771: "it still shows
 * the same thing."
 *
 * v1.6.1771 treated the symptom. It kept asking every row "who is this
 * customer?" and merely widened the hunt for a name, so an order, an
 * out-of-stock, a holiday or a Happy Logistics alert — which carry no customer
 * name or phone AT ALL, deliberately — still fell through to "Unknown number".
 * There was never a name to find.
 *
 * `GET /v1/tenant/notifications` now returns five-plus kinds. Only ONE of them
 * is about a person: `happytexting.inbound_reply`. Every other kind already
 * carries a `title` that says what it is — "New order ORD-000000901", "Happy
 * Logistics problem at MEALBOX" — and that is the right headline.
 *
 * ── WHY THIS IS NOT A PLAIN EQUALITY CHECK ───────────────────────────────
 *
 * Two things make the type untrustworthy on its own:
 *
 *  1. LEGACY ROWS. The contract says `general` "also matches historical rows
 *     that stored a class name instead of a slug". Rows written before the
 *     slugs existed were all texting replies, because the bell WAS the texting
 *     inbox — so a row with no recognisable type that carries a name or a
 *     number is still a reply, and must keep its name line.
 *  2. NEW TYPES. The Happy Logistics alerts in this very ticket are not in the
 *     contract's enum yet. An unknown type must therefore fall to the TITLE
 *     branch, not the person branch — which is also what makes a type
 *     introduced tomorrow render correctly without a release here.
 *
 * So: a known non-reply type is never a person; an unknown type is a person
 * only if the payload actually describes one. Both defaults fail towards the
 * title, which is the branch that cannot produce "Unknown number".
 */
export const REPLY_TYPE = 'happytexting.inbound_reply';

/** The kinds the contract enumerates that are explicitly NOT about a person. */
export const NON_REPLY_TYPES: readonly string[] = [
  'order.created',
  'product.out_of_stock',
  'store.holiday_upcoming',
  // v1.6.1817 — Asana 1219845145512005. A card-testing run is about a BURST
  // of orders, not a person, so it must not fall through to the
  // "has it got a phone or a name?" guess below. It would currently come out
  // right by accident (the payload carries neither), and an accident is not
  // a contract: a later payload carrying, say, a customer_id would silently
  // turn the alert into "Unknown number".
  'checkoutguard.security_alert',
  // Asana 1220202714832233 — about an order, never a person.
  'order.review_waiting',
];

/**
 * v1.6.1817 — the icon for a row in the bell (Asana 1219845145512005).
 *
 * "The inbox type filter and icon cover checkoutguard.security_alert."
 *
 * Until now every row showed the same unread dot and nothing else, so a
 * security alert and a texting reply were indistinguishable at a glance —
 * which is the whole point of an alert that says a checkout was paused.
 *
 * An unknown type gets the neutral bell rather than no icon, so a kind this
 * build has never heard of still lines up with the rest of the list.
 */
export function notificationIcon(row: TenantNotificationRow): string {
  const type = (row?.data?.type ?? '').toString().trim();
  switch (type) {
    case 'checkoutguard.security_alert': return 'fa-solid fa-shield-halved';
    case 'order.created':                return 'fa-solid fa-receipt';
    case 'product.out_of_stock':         return 'fa-solid fa-box-open';
    case 'store.holiday_upcoming':       return 'fa-solid fa-calendar-day';
    case 'order.review_waiting':         return 'fa-solid fa-hourglass-half';
    case REPLY_TYPE:                    return 'fa-solid fa-comment-dots';
    default:
      // A legacy reply has no type but does have a person on it.
      return isReplyNotification(row) ? 'fa-solid fa-comment-dots' : 'fa-solid fa-bell';
  }
}

/**
 * The tone of the icon. Only the security alert is coloured: a list where
 * every row is coloured has no emphasis left to give the one that matters.
 */
export function notificationIconTone(row: TenantNotificationRow): string {
  const type = (row?.data?.type ?? '').toString().trim();
  return type === 'checkoutguard.security_alert'
    ? 'text-amber-600 bg-amber-50'
    : 'text-gray-400 bg-gray-100';
}

/**
 * v1.6.1817 — the one-line summary under a security alert's title.
 *
 * The body the backend sends is the full explanation and is already shown.
 * This is the part staff act on: which paid orders were REMOVED, because the
 * card processor flagged them and they have to be refunded in the processor's
 * own portal. A removed order disappears from the order lists, so if this
 * line does not name it, nothing else will.
 */
export function securityAlertRemovedNote(row: TenantNotificationRow): string {
  const d: any = row?.data ?? {};
  if ((d.type ?? '') !== 'checkoutguard.security_alert') return '';
  const numbers: string[] = Array.isArray(d.removed_order_numbers)
    ? d.removed_order_numbers.map((n: any) => String(n ?? '').trim()).filter(Boolean)
    : [];
  const count = Number(d.removed_count);
  if (!numbers.length && !(Number.isFinite(count) && count > 0)) return '';
  const named = numbers.length ? numbers.join(', ') : `${count} order${count === 1 ? '' : 's'}`;
  return `Removed and needs refunding in your card processor: ${named}.`;
}

export function isReplyNotification(row: TenantNotificationRow): boolean {
  const type = (row?.data?.type ?? '').toString().trim();
  if (type === REPLY_TYPE) return true;
  if (NON_REPLY_TYPES.includes(type)) return false;
  // `general`, an empty type, or a kind this version has never heard of.
  // A legacy reply still has a person on it; a new alert does not.
  return !!notificationPhone(row) || !!namedCustomer(row);
}

/** The customer's name as the payload spells it, or '' — no fallbacks. */
function namedCustomer(row: TenantNotificationRow): string {
  const d: any = row?.data ?? {};
  const first = (v: any) => (v ?? '').toString().trim();
  return first(d.customer_name)
    || [first(d.contact_first_name), first(d.contact_last_name)].filter(Boolean).join(' ').trim()
    || first(d.name)
    || first(d.customer?.name);
}

/**
 * The headline for a notification that is not about a person.
 *
 * The title is what the backend already wrote for exactly this purpose. The
 * message is a fallback for a row that somehow has none, and the generic last
 * resort is deliberately not "Unknown" anything — an alert whose title failed
 * to arrive is still a real alert, and calling it unknown is the bug this
 * ticket is about.
 */
export function notificationHeadline(row: TenantNotificationRow): string {
  const title = (row?.title ?? '').toString().trim();
  if (title) return title;
  const message = (row?.message ?? '').toString().trim();
  if (message) return message;
  return 'Notification';
}

export function notificationWho(row: TenantNotificationRow): string {
  // v1.6.1792 — only a texting reply is about a person. Everything else says
  // what it is, from its own title.
  if (!isReplyNotification(row)) return notificationHeadline(row);

  const named = namedCustomer(row);
  if (named) return named;

  // No name anywhere: the number identifies them better than "unknown" does.
  // Reached only for a genuine reply, where an unknown number really is the
  // subject — which is what this string was written for.
  const phone = notificationPhone(row);
  return phone || 'Unknown number';
}

/**
 * v1.6.1771 — the phone number for the second line of an alert.
 *
 * "can we make it so that it says the name of the customer and then the phone
 * number in smaller and softer text under or beside the name".
 *
 * Returns '' when the payload has no number, and also when the number IS the
 * headline — repeating it under itself would be noise.
 */
export function notificationPhone(row: TenantNotificationRow): string {
  const d: any = row?.data ?? {};
  const raw = (d.customer_phone ?? d.phone ?? d.contact_phone ?? d.from ?? d.customer?.phone ?? '')
    .toString().trim();
  return raw;
}

/** The phone to show BENEATH the name — blank when it is already the name. */
export function notificationSubline(row: TenantNotificationRow): string {
  // v1.6.1792 — the number belongs under a PERSON's name. On an order or a
  // stock alert the headline is a title, and a phone number under it would
  // read as the title's phone number.
  if (!isReplyNotification(row)) return '';
  const phone = notificationPhone(row);
  if (!phone) return '';
  return notificationWho(row) === phone ? '' : phone;
}
